import {
  BookingSourceType, BookingState, Gym, GymTypes, ILeaderboardInstance, ILiveStreamOptions,
  IMetricResult, IUserWorkoutSessionMemberReport, IUserWorkoutSessionSummary, IWorkoutDates, IWorkoutImportContext,
  LeaderboardMetric, RemovedReasonType, TimeSpan,
} from 'fitbeat.models';
import { BookingsConfig } from '../../config/bookings';
import { WorkoutType } from '../../config/workouttype';
import { ScheduleExercises } from '../../logic/gymscheduling/scheduleexercises';
import { DbGymRepository } from '../../persistence/repositories';
import { Mapper } from '../../persistence/serialization/mapper';
import { CardioOrResistance } from '../exercise';
import { WorkoutSettings } from '../user/workoutsettings';
import { IUserRating } from '../workout/userrating';
import { HomeWorkoutSequence } from './homeworkoutsequence';
import { InternalWorkoutReview } from './internalworkoutreview';
import { UserExerciseSession } from './userexercisesession';
import { AreaCategory, getAreaCategory } from '../../config';

export interface IExternalReference {
  /**
   * unique id for a booking request
   * generated by ClassPass
   */
  classPassReservationId?: string;
}

interface ICreateParams {
  amountOfStages: number;
  settings?: WorkoutSettings;
  currentStage?: number;
  earliestAllowedStartTime?: Date;
  provisional?: BookingState;
  creationDate?: Date;
  gym?: Gym;
  externalReference?: IExternalReference;
  uniqueReferenceSuffix?: string;
}

interface ICreateSessionForSchedulingCheckParams {
  earliestAllowedStartTime: Date;
  workoutType: WorkoutType;
  gym: Gym;
}

type GymByName = (name: string) => Gym;

export class UserWorkoutSession implements IUserWorkoutSessionMemberReport, IWorkoutDates, IUserWorkoutSessionSummary {
  public static WorkoutDescriptionMap: Map<CardioOrResistance, string> = new Map([
    [CardioOrResistance.Cardio, 'Burn workout: fast and light'],
    [CardioOrResistance.Resistance, 'Build workout: slow and heavy'],
    [CardioOrResistance.Balance, 'Balance workout: mobility and core'],
  ]);

  public static create(userProfileId: string, params: ICreateParams): UserWorkoutSession {
    return new UserWorkoutSession(
      userProfileId,
      params.amountOfStages,
      params.settings,
      params.currentStage,
      params.earliestAllowedStartTime,
      params.provisional,
      params.creationDate,
      params.gym,
      params.externalReference,
      undefined, // livestreamOptions
      undefined, // staticZoneNumber
      params.uniqueReferenceSuffix,
    );
  }

  public static isFitillionNonImportedWorkout(workout: UserWorkoutSession): boolean {
    return !workout.importOrigin;
  }

  public static hasEffortPoints(workout: UserWorkoutSession): boolean {
    return !!(workout.performanceMetrics && workout.performanceMetrics.load);
  }

  public static CreateSessionForSchedulingChecks(params: ICreateSessionForSchedulingCheckParams): UserWorkoutSession {
    const workoutSession = new UserWorkoutSession(
      'placeholderUserId',
      params.workoutType,
    );
    workoutSession.gymName = params.gym.name;
    workoutSession.earliestAllowedStartTime = params.earliestAllowedStartTime;
    return workoutSession;
  }

  public static getWorkoutDescription(session: UserWorkoutSession | undefined): string {
    if (session != null && session.settings != null) {
      const workoutDescription = this.WorkoutDescriptionMap.get(session.settings.cardioOrResistance);
      if (workoutDescription) {
        return workoutDescription;
      }
    }
    return this.WorkoutDescriptionMap.get(CardioOrResistance.Cardio)!;
  }

  public static hasPairedWorkoutSession(session: UserWorkoutSession): boolean {
    return session.pairedSessionReference !== undefined;
  }

  public static isPairedSessionConfirmedOrProvisional(session: UserWorkoutSession | undefined): boolean {
    if (!session) {
      return false;
    }

    if (!session.pairedSession) {
      return false;
    }

    return session.pairedSession.bookingState === BookingState.ConfirmedBooking
      || session.pairedSession.bookingState === BookingState.BookingInvite;
  }

  public static isWorkoutSessionConfirmed(session: UserWorkoutSession): boolean {
    return session.bookingState === BookingState.ConfirmedBooking;
  }

  public static hasPairedSession(session: UserWorkoutSession): boolean {
    return !!(session.pairedSessionReference && session.pairedSession);
  }

  public static getOpenInviteLink(
    session: UserWorkoutSession,
  ): string | undefined {
    return (session.openInviteLink || session.pairedSession?.openInviteLink);
  }

  public static isHomeGroupBooking(
    session: UserWorkoutSession,
    gymByName: GymByName = DbGymRepository.getByName,
  ): boolean {
    return UserWorkoutSession.isHomeGroupInviteeWorkout(session, gymByName)
      || UserWorkoutSession.isHomeGroupCallCreatorWorkout(session, gymByName);
  }

  public static isHomeGroupInviteeWorkout(session: UserWorkoutSession, gymByName: GymByName): boolean {
    return this.isHomeBooking(session, gymByName) && !!session.pairedSessionReference;
  }

  public static isHomeGroupCallCreatorWorkout(session: UserWorkoutSession, gymByName: GymByName): boolean {
    return this.isHomeBooking(session, gymByName) && !!(session.openInviteLink);
  }

  public static getOpenInviteReference(
    session: UserWorkoutSession,
    gymByName: GymByName = DbGymRepository.getByName,
  ): string | undefined {
    if (!UserWorkoutSession.isHomeGroupBooking(session, gymByName)) {
      return;
    }
    return session.pairedSessionReference || session.uniqueReference;
  }

  public static getWorkoutDurationInMs(session: UserWorkoutSession): number {
    const startTime = new Date(
      session.originalStartTimeOfFirstExercise || session.startTimeOfFirstExercise,
    );
    const endTime = new Date(
      session.originalEndTime || session.endTime,
    );
    return endTime.getTime() - startTime.getTime();
  }

  /**
   * Use this when you want a workout to be processed for completion again
   */
  public static markForCompletionReprocessing(session: UserWorkoutSession): void {
    if (session.workoutMarkedAsCompletedAt !== undefined && session.workoutMarkedAsCompletedAt !== false) {
      session.workoutMarkedAsCompletedAt = false;

      // if it was removed due to overlap, we need to give the chance for processing again
      // since the overlapping workout might score worse that this one on reprocess
      if (session.removedAt && session.removedReason === RemovedReasonType.Overlapping) {
        session.removedReason = undefined;
        session.removedAt = false;
      }
    }
  }

  public static markSessionAsCheckedIn(
    session: UserWorkoutSession,
    skippedBiometric: boolean,
    checkInTime: Date,
    workoutSelectionId: string,
  ): void {
    session.checkedIn = true;
    session.checkinSkippedBiometric = skippedBiometric;
    session.gymCheckinTime = checkInTime;
    session.workoutSelectionId = workoutSelectionId;
  }

  public static isStreamingWorkoutAndNotHost(session: { isStreamingWorkout?: boolean,
    pairedSessionReference?: string }): boolean {
    return !!session.pairedSessionReference && !!session.isStreamingWorkout;
  }

  public static isStreamingWorkoutAndIsHost(session: UserWorkoutSession | HomeWorkoutSequence): boolean {
    return !!session.isStreamingWorkout && !session.pairedSessionReference;
  }

  public static isHomeBooking(session: UserWorkoutSession, gymByName: GymByName) {
    const gym = gymByName(session.gymName);
    return gym.type === GymTypes.Home;
  }

  public static getFirstNonPauseExerciseSession(session: UserWorkoutSession): UserExerciseSession | undefined {
    return session.exerciseSessions.find((exerciseSession) => !exerciseSession.isPause);
  }

  public static getStartingZoneDisplayNumber(session: UserWorkoutSession | null): string {
    if (!session) {
      return '-';
    }
    const firstNonPauseExerciseSession = UserWorkoutSession.getFirstNonPauseExerciseSession(session);
    return firstNonPauseExerciseSession?.zone.displayNumber || '-';
  }

  public static calculateWorkoutDurationInMinutes(
    startTimeOfFirstExercise: Date,
    endTime: Date,
    additionalSecondsBeforeFirstExercise = 0,
  ): number {
    const additionalMs = additionalSecondsBeforeFirstExercise * 1000;
    const endTimeMs = new Date(endTime).getTime();
    const startTimeMs = new Date(startTimeOfFirstExercise).getTime();
    const totalMs = endTimeMs - startTimeMs - additionalMs;
    const totalMinutes = totalMs / 60000;

    return totalMinutes;
  }

  public readonly uniqueReference: string; // saved as _Id

  public userProfileId: string; // user _Id

  public externalReference?: IExternalReference;

  // workout info
  public workoutType: WorkoutType;

  public settings: WorkoutSettings; // saved and used to calc workout

  public recommendedSettings?: WorkoutSettings; // transient and optional - generated on the fly

  public exerciseSessions = new Array<UserExerciseSession>();

  public bookingSource: BookingSourceType;

  // date times
  /**
   * The start time of the workout session.
   *
   * For studio sessions, this represents the time when the pre-workout zone begins,
   *  which is always earlier than `startTimeOfFirstExercise`.
   * For home sessions, it indicates the booking time when users can join the workout from the app,
   *  potentially joining a group call a few minutes early to chat.
   *
   * Note: This time may advance if the workout is paused, which could lead to misleading reporting.
   */
  public startTime: Date;

  /**
   * The time at which the warmup (aka first exercise) starts for both studio and home sessions.
   * This is when the user actually starts doing exercises
   * One exception is if there is talks at the start of a home influcer workout:
   *  In that case startTimeOfFirstExercise would be the start of the talk and not of the warmup
   * Note: This time moves on when paused, which might be misleading in reporting.
   */
  public startTimeOfFirstExercise: Date; // differs from startTime only if pause at start

  /**
   * The time the warmup/workout actually ends, excluding a few seconds at the end of the cooldown sequence.
   * In home workouts, this time will move on with pauses or when adding time for talks.
   * This is crucial for home workouts, as timings are calculated back from this endTime.
   * The difference between the endTime and startTimeOfFirstExercise should always equal the duration of the sequences.
   */
  public endTime: Date;

  /**
   * Original times:
   * - For booked workouts, the times when the workout was originally booked.
   * - For on-demand home bookings, the initial hydration time.
   * These values don't shift when the start and end times shift and are useful for reporting.
   */
  public originalStartTimeOfFirstExercise?: Date;

  public originalStartTime?: Date;

  public originalEndTime?: Date;

  /**
   * @description The time at which the studio checked in, or home users started.
   */
  public gymCheckinTime?: Date;

  /**
   * The time at which a home user started the workout.
   * @deprecated It would have been preferable to use the `gymCheckinTime` instead.
   */
  public homeWorkoutStartedAt?: Date;

  /**
   * The time at which the workout is deemed removed. This could be if a workout is removed
   * for whatever reason before or during the workout, such as if a home user chooses to cancel
   * without saving, or if the studio user doesn't check in and gets automatically removed after some time.
   * @type {Date | false}
   * @default false
   */
  public removedAt: Date | false = false;

  /**
   * @description Indicates why the `removedAt` timestamp was set.
   */
  public removedReason?: RemovedReasonType;

  /**
   * @description if this workout was removed due to overlap, this field will contain the id
   * of the workout that overlapped it and was saved instead
   */
  public overlappedByWorkoutId?: string;

  /**
   * The date and time when the booking was created.
   */
  public createdAt: Date;

  /**
   * @description The additional seconds added after the startTimeOfFirstExercise before the first exercise starts
   */
  public additionalSecondsBeforeFirstExercise?: number;

  /**
   * @description This field was created for on-demand studio workouts to indicate when the user wants to start,
   * which may differ from the actual start time. A more appropriate name for this field would be `requestedStartTime`.
   */
  public earliestAllowedStartTime: Date;

  /**
   * @description The offset in minutes between the gym's local time and UTC. This field is primarily used
   * to assist in generating views in CouchDB where it's not possible to calculate the gym's timezone.
   * Note that JavaScript Date objects are timezone-agnostic, and timezone handling is usually determined
   * by the gym's location, making this field less critical for most logic.
   */
  public timezoneOffset?: number;

  // studio partner booking related fields
  public bookingState: BookingState = BookingState.ConfirmedBooking;

  /**
   * The time at which a studio invite expires and the slot opens up for others to book.
   * This property is related to studio invites and only applies to workouts not in the BookingInvite state.
   */
  public expiresAt?: Date;

  public expiryNotified = false;

  public reviewNotified = false;

  public checkInNotified: false | Date = false;

  public pairedSession?: UserWorkoutSession;

  public pairedSessionReference?: string;

  public pairedSessionUserProfileId?: string;

  public openInviteLink: string;

  public internalWorkoutReview: InternalWorkoutReview;

  public numberOfOpenSlotsAtTimeOfBooking: number;

  public isPeak: boolean;

  public checkedIn = false;

  public checkinSkippedBiometric = false;

  public anonymised = false;

  public performanceMetrics: IMetricResult | null = null;

  public currentStage = 1; // todo this is just used as an algo iterater and there is no need to store it

  public gymName: string;

  public workoutSelectionId: string;

  public userRating: IUserRating;

  public isInstant: boolean;

  public isDemo: boolean;

  public isStreamingWorkout?: boolean;

  public memberMainGym?: string;

  public livestreamOptions?: ILiveStreamOptions;

  public leaderboards?: ILeaderboardInstance[];

  public areaCategory?: AreaCategory;

  /**
   * undefined if not an import, ie Fitillion workout
   */
  public importOrigin?: IWorkoutImportContext;

  /**
   * @description Helper field used to determine if a workout counts as completed. If
   * this field is not set, and it is past the endDate of the workout, the workout will be marked
   * as removed.
   */
  public workoutMarkedAsCompletedAt?: Date | false;

  constructor(
    userProfileId: string,
    amountOfStages: number,
    settings?: WorkoutSettings,
    currentStage = 1,
    earliestAllowedStartTime: Date = new Date(),
    provisional: BookingState = BookingState.ConfirmedBooking,
    creationDate?: Date,
    gym: Gym = DbGymRepository.getTestGym(),
    externalReference?: IExternalReference,
    livestreamOptions?: ILiveStreamOptions,
    staticZoneNumber?: number,
    uniqueReferenceSuffix?: string,
  ) {
    if (creationDate) {
      this.createdAt = creationDate;
    }
    this.userProfileId = userProfileId;
    if (settings instanceof WorkoutSettings) {
      this.settings = settings;
    }
    this.gymName = gym.name;
    this.earliestAllowedStartTime = earliestAllowedStartTime;
    this.currentStage = currentStage;
    this.uniqueReference = Date.now() + (userProfileId || 'anon');
    this.workoutType = this.getWorkoutTypeFromNumber(amountOfStages);
    this.bookingState = provisional;
    this.externalReference = externalReference;
    this.livestreamOptions = livestreamOptions;
    this.areaCategory = getAreaCategory(staticZoneNumber);

    ScheduleExercises.scheduleExercisesInWorkoutConstructor(this, currentStage, gym, staticZoneNumber);
    if (provisional === BookingState.ProvisionalBooking) {
      this.uniqueReference += '_provisional';
    }
    if (this.isProvisionalInvite()) {
      const startTimebuffer = new Date(this.earliestAllowedStartTime);
      startTimebuffer.setMinutes(
        startTimebuffer.getMinutes() + BookingsConfig.minutesGraceTimeForBookingsAfterClassStarted,
      );
      this.expiresAt = TimeSpan.min(new Date((creationDate || new Date()).getTime()
        + (BookingsConfig.provisionalBookingExpiryInHours * 60 * 60 * 1000)), startTimebuffer);
    }
    this.workoutMarkedAsCompletedAt = false;

    if (uniqueReferenceSuffix) {
      this.uniqueReference += uniqueReferenceSuffix;
    }
  }

  public getPauses(): number[] {
    if (this.exerciseSessions == null || this.exerciseSessions.length === 0) {
      return [];
    }
    const pauses: number[] = [];
    for (const exerciseSession of this.exerciseSessions) {
      if (exerciseSession.isPause) {
        pauses.push(exerciseSession.workoutStage);
      }
    }
    return pauses;
  }

  public canChangePartner(): boolean {
    if (this.pairedSession == null) {
      return false;
    }
    return this.pairedSession.bookingState === BookingState.ProvisionalBooking;
  }

  public canInviteNewPartner(): boolean {
    if (this.pairedSession == null) {
      return false;
    }
    return (
      this.pairedSession.bookingState === BookingState.BookingInvite
        || this.pairedSession.bookingState === BookingState.ProvisionalBooking);
  }

  public isProvisionalInvite(): boolean {
    return this.bookingState === BookingState.BookingInvite || this.bookingState === BookingState.ProvisionalBooking;
  }

  public canCancelInite(): boolean {
    return this.canChangePartner();
  }

  public canCancel(): boolean {
    return true;
  }

  public amountOfStages(): number {
    if (this.exerciseSessions == null) {
      return 0;
    }
    return this.exerciseSessions.length;
  }

  public getWaitTimeInSeconds() {
    return Math.abs(this.earliestAllowedStartTime.getTime() - this.startTime.getTime()) / 1000;
  }

  public makeSerializable() {
    return Mapper.makeSerializable(this);
  }

  public isSameDay(date: Date, timezone?: string): boolean {
    if (timezone) {
      return TimeSpan.areOnSameDayInTimeZone(this.startTime, date, timezone);
    }
    return TimeSpan.areOnSameDateSameDay(this.startTime, date);
  }

  public calculateNumberOfSessionsRemainingIncludingCurrentSession(cycleTimeInSeconds: number, atDate: Date): number {
    const elapsedTimeInMs = atDate.getTime() - this.startTime.getTime();
    if (elapsedTimeInMs <= 0) { // exercise hasn't started yet
      return this.exerciseSessions.length;
    }
    const elapsedSessions = Math.floor(elapsedTimeInMs / (cycleTimeInSeconds * 1000));
    return this.exerciseSessions.length - elapsedSessions;
  }

  public clearPairedSessionDetailsIfPartnerBooking() {
    const isPartnerBookingProvisional = this.userProfileId === this.pairedSessionUserProfileId;
    if (isPartnerBookingProvisional) {
      this.pairedSessionUserProfileId = undefined;
      this.pairedSessionReference = undefined;
      this.pairedSession = undefined;
    }
  }

  public calculateWeightedWorkoutScore(
    teamSize: number,
    metric: LeaderboardMetric,
    calculatedScore?: number,
  ): number {
    switch (metric) {
      case 'team-weighted-minutes-score':
        const totalMinutes = this.calculateWorkoutDurationInMinutes();
        if (calculatedScore) {
          return Number((calculatedScore / teamSize).toFixed(2));
        }
        return Number((totalMinutes / teamSize).toFixed(2));
      case 'team-weighted-points-score':
        if (calculatedScore) {
          return Number((calculatedScore / teamSize).toFixed(2));
        }
        if (this.performanceMetrics && this.performanceMetrics.load) {
          return Number((this.performanceMetrics.load / teamSize));
        }
        return 0;
      default:
        return 0;
    }
  }

  public calculateWorkoutDurationInMinutes(): number {
    const additionalMs = this.additionalSecondsBeforeFirstExercise
      ? this.additionalSecondsBeforeFirstExercise * 1000
      : 0;
    const endTimeMs = new Date(this.endTime).getTime();
    const startTimeMs = new Date(this.startTimeOfFirstExercise).getTime();
    const totalMs = endTimeMs - startTimeMs - additionalMs;
    const totalMinutes = totalMs / 60000;

    return totalMinutes;
  }

  private getWorkoutTypeFromNumber(n: number): WorkoutType {
    return WorkoutType[WorkoutType[n]] || n;
  }
}
